import React, { lazy, Suspense, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Route, useLocation, Routes as Switch } from 'react-router-dom';
import ReactResizeDetector from 'react-resize-detector';
import Modal from 'components/containers/Modal/Modal';
import {
  getLoadingSelector,
  isPreviewSelector,
  isSdkSelector
} from 'redux/selectors/global';
import ErrorBoundary from 'components/containers/ErrorBoundary/ErrorBoundary';
import { sendParentMessage } from 'services/iframeService';
import Spinner from 'components/widgets/Spinner/Spinner';
import {
  APP_ROUTE_NOT_FOUND_ROUTE,
  CARD_TOKENIZATION_ROUTE,
  CONNECTORS_ERROR_PAGE_ROUTE,
  CUSTOMER_ROUTE,
  DISBURSE_ROUTE,
  ERROR_500_ROUTE,
  HOSTED_CARD_TOKENIZATION_ROUTE,
  HOSTED_PAGE_PREVIEW_ROUTE,
  IDV_ROUTE,
  ISSUING_ROUTE,
  OTP_ROUTE,
  PAY_SHORTLINK,
  REUSABLE_LINK_ROUTE,
  SUBSCRIPTION_ROUTE,
  THREE_DS_PAYMENT_ROUTE,
  COMPLETE_BANK_PAYMENT_ROUTE,
  COMPLETE_BANK_REDIRECT_ROUTE,
  VALITOR_ROUTE,
  STATIC_PAGES_ROUTES,
  DEMO_ROUTE,
  HOSTED_DISBURSE_CARD_TOKENIZATION_ROUTE
} from 'routes/paths';
import { useCSSInjectionTracking } from 'hooks';
import { CustomToastContainer } from '@rapyd/react-ui-library';
import { ThemeFamilies } from '@rapyd/react-ui-library/dist/theme/families';
import { SimulatorPageType } from 'components/containers/Simulators/CompleteBankSimulator';

const CheckoutRoutes = lazy(() => import('routes/Collect/CheckoutRoutes'));
const DisburseRoutes = lazy(() => import('routes/Disburse/DisburseRoutes'));
const CardTokenizationRoutes = lazy(() => import('routes/Collect/CardRoutes'));
const IssuingRoutes = lazy(() => import('routes/Issuing/IssuingRoutes'));
const IDVRoutes = lazy(() => import('routes/IDV/IDVRoutes'));
const SubscriptionRoutes = lazy(() => import('routes/Subscription/SubscriptionRoutes'));
const ValitorRoutes = lazy(() => import('routes/Valitor/ValitorRoutes'));
const Error404 = lazy(
  () => import('components/widgets/GeneralPages/ErrorPages/Error404')
);
const GeneralError = lazy(
  () => import('components/widgets/GeneralPages/ErrorPages/GeneralError')
);
const HostedCardTokenization = lazy(() => import('components/containers/Hosted/Hosted'));
const HostedPayoutCardTokenization = lazy(
  () =>
    import(
      'components/containers/Hosted/payoutCardTokenization/HostedPayoutCardTokenization'
    )
);
const ThreeDSPayment = lazy(
  () => import('components/containers/Simulators/ThreeDSPayment')
);
const CompleteBankSimulator = lazy(
  () => import('components/containers/Simulators/CompleteBankSimulator')
);
const OTPMain = lazy(() => import('components/containers/OTP'));
const HostedPagePreview = lazy(
  () => import('components/containers/Preview/HostedPagePreview')
);
const ReusableLinkRoutes = lazy(() => import('routes/ReusableLink'));
const GenericPay = lazy(() => import('components/containers/Pay/Pay'));
const CustomerTokenizationRoutes = lazy(
  () => import('routes/CustomerTokenization/CustomerTokenizationRoutes')
);
const ErrorPages = lazy(() => import('components/widgets/GeneralPages/ErrorPages/index'));
const StaticPagesRoutes = lazy(() => import('routes/StaticPages/StaticPagesRoutes'));

const DemoRoutes = lazy(() => import('routes/demo/DemoRoutes'));

const onResize = (width?: number, height?: number) => {
  sendParentMessage('ON_RESIZE', { height, width });
};

const Hosted = () => {
  const { loading, loadingProps } = useSelector(getLoadingSelector);
  const isSdk = useSelector(isSdkSelector);
  const isPreview = useSelector(isPreviewSelector);
  const contentRef = useRef<HTMLElement>(null);
  const location = useLocation();
  useCSSInjectionTracking();

  return (
    <ErrorBoundary>
      <Suspense fallback={isSdk ? null : <div />}>
        <Switch>
          <Route path={HOSTED_PAGE_PREVIEW_ROUTE} element={<HostedPagePreview />} />
          <Route
            path={HOSTED_CARD_TOKENIZATION_ROUTE}
            element={<HostedCardTokenization />}
          />
          <Route
            path={HOSTED_DISBURSE_CARD_TOKENIZATION_ROUTE}
            element={<HostedPayoutCardTokenization />}
          />
          <Route path={THREE_DS_PAYMENT_ROUTE} element={<ThreeDSPayment />} />
          <Route
            path={COMPLETE_BANK_PAYMENT_ROUTE}
            element={<CompleteBankSimulator type={SimulatorPageType.BankPayment} />}
          />
          <Route
            path={COMPLETE_BANK_REDIRECT_ROUTE}
            element={<CompleteBankSimulator type={SimulatorPageType.BankRedirect} />}
          />
          <Route path={CONNECTORS_ERROR_PAGE_ROUTE} element={<GeneralError />} />
          <Route path={ERROR_500_ROUTE} element={<ErrorPages {...location?.state} />} />
          <Route path={APP_ROUTE_NOT_FOUND_ROUTE} element={<Error404 />} />
          <Route path={VALITOR_ROUTE} element={<ValitorRoutes />} />
          <Route path={DISBURSE_ROUTE} element={<DisburseRoutes />} />
          <Route path={CARD_TOKENIZATION_ROUTE} element={<CardTokenizationRoutes />} />
          <Route path={ISSUING_ROUTE} element={<IssuingRoutes />} />
          <Route path={IDV_ROUTE} element={<IDVRoutes />} />
          <Route path={SUBSCRIPTION_ROUTE} element={<SubscriptionRoutes />} />
          <Route path={OTP_ROUTE} element={<OTPMain />} />
          <Route path={REUSABLE_LINK_ROUTE} element={<ReusableLinkRoutes />} />
          <Route path={PAY_SHORTLINK} element={<GenericPay />} />
          <Route path={CUSTOMER_ROUTE} element={<CustomerTokenizationRoutes />} />
          <Route path={STATIC_PAGES_ROUTES} element={<StaticPagesRoutes />} />
          <Route path={DEMO_ROUTE} element={<DemoRoutes />} />
          <Route path="*" element={<CheckoutRoutes />} />
        </Switch>
      </Suspense>
      <ReactResizeDetector
        handleHeight
        handleWidth
        targetRef={contentRef}
        onResize={(width?: number, height?: number) =>
          !isPreview && isSdk && onResize(width, height)
        }
      />
      {loading && !isSdk && <Spinner {...loadingProps} />}
      <Modal themeFamily={ThemeFamilies.PRIMARY} primaryButtonProps={{}} />
      <CustomToastContainer />
    </ErrorBoundary>
  );
};

export default Hosted;
